import React, {
  Dispatch, SetStateAction, useEffect, useRef, useState,
} from 'react';
import {
  View, ScrollView, StyleSheet, Text, TouchableOpacity, Platform,
} from 'react-native';
import { useSelector } from 'react-redux';
import Colors from '../../constants/Colors';
import GridIconSmall from '../12pxIcons/GridIconSmall';
import {
  AssetInstance, Template, TemplateGroup, AssetCustomField,
} from '../../constants/Types';
import getAssets from '../../requests/Assets/GetAssets';

interface SearchResultsPaneProps {
  focused?: boolean,
  blur?: () => void,
  searchValue: string,
  assets?: AssetInstance[],
  templates: Template[],
  templateGroups: TemplateGroup[],
  selectAsset: Dispatch<SetStateAction<AssetInstance>>,
  selectTemplate: Dispatch<SetStateAction<Template>>,
  selectTemplateGroup: Dispatch<SetStateAction<string>>,
  showAssets: Dispatch<SetStateAction<boolean>>,
  setSearchValue: Dispatch<SetStateAction<string>>,
  selectedAsset: AssetInstance,
  selectedTemplate: Template,
  selectedTemplateGroup: string,
  assetsShown: boolean,
}

const SearchResultsPane = ({
  searchValue, assets, templates, templateGroups, selectedAsset, selectedTemplate, assetsShown,
  selectedTemplateGroup, focused, blur = null, selectTemplate, selectAsset, showAssets,
  selectTemplateGroup, setSearchValue,
}: SearchResultsPaneProps) => {
  const selectedBuildingId: string = useSelector((state:
    { selectedBuilding: { id: string } }) => state.selectedBuilding.id);
  const searchString: string = searchValue?.toString().toLowerCase().trim();
  const [childHeight, setChildHeight] = useState<number>(0);
  const [focusIndex, setFocusIndex] = useState<number>(-1);
  const refs = [];
  const [searchedAssets, setSearchedAssets] = useState<AssetInstance[]>([]);
  const filteredAssets: AssetInstance[] = assets?.filter((asset: AssetInstance) => {
    let match: boolean = false;
    asset.customFields?.forEach((field: AssetCustomField) => {
      if (field.value?.toString().toLowerCase().includes(searchString)) match = true;
    });
    if (asset.name?.toString().toLowerCase().includes(searchString)
      || asset.manufacturer?.toString().toLowerCase().includes(searchString)
      || asset.description?.toString().toLowerCase().includes(searchString)
      || asset.model?.toString().toLowerCase().includes(searchString)
      || asset.serial?.toString().toLowerCase().includes(searchString)
      || asset.id?.toString().toLowerCase().includes(searchString)
      || asset.templateName?.toString().toLowerCase().includes(searchString)
      || asset.locationSpace?.toString().toLowerCase().includes(searchString)
      || asset.locationFloor?.toString().toLowerCase().includes(searchString)
      || asset.locationArea?.toString().toLowerCase().includes(searchString)) return true;
    return match;
  });
  const filteredTemplates: Template[] = templates?.filter((template: Template) => template.name
    ?.toLowerCase().includes(searchString));
  const filteredTemplateGroups: TemplateGroup[] = templateGroups
    ?.filter((group) => group.Name?.toLowerCase().includes(searchString)) ?? [];

  const shiftFocus = (e) => {
    if (e.code === 'ArrowDown') {
      setFocusIndex(Math.min(focusIndex + 1, (filteredAssets?.length ?? 0)
        + (filteredTemplates?.length ?? 0) + (filteredTemplateGroups?.length ?? 0) - 1));
    } else if (e.code === 'ArrowUp') {
      if (focusIndex <= 0) blur();
      else setFocusIndex(Math.max(-1, focusIndex - 1));
    }
  };
  const shiftFocusRef = useRef(shiftFocus);

  useEffect(() => {
    // eslint-disable-next-line no-undef
    if (Platform.OS === 'web') window.addEventListener('keydown', (e) => shiftFocusRef.current(e));
  }, []);

  useEffect(() => {
    if (focused) refs[0]?.focus();
  }, [focused]);

  useEffect(() => {
    if (!(selectedBuildingId && searchString?.trim())) return;
    getAssets({
      locationId: selectedBuildingId,
      enableSubLocationsLookUp: true,
      textSearch: searchString,
    }).then((res) => {
      const assetIds: string[] = (assets ?? []).map((asset: AssetInstance) => asset.id);
      setSearchedAssets(res.assets
        ?.filter((asset: AssetInstance) => !assetIds.includes(asset.id)) ?? []);
    });
  }, [searchString, selectedBuildingId]);

  useEffect(() => {
    shiftFocusRef.current = shiftFocus;
    refs[focusIndex]?.focus();
  }, [focusIndex]);

  return searchString?.length
    ? <View style={[styles.wrapper, Platform.OS === 'android' && { height: childHeight }]}>
      <View style={styles.container} onLayout={(e) => {
        if (Platform.OS === 'android') setChildHeight(e.nativeEvent.layout.height);
      }}>
        <ScrollView style={styles.content}>
          {filteredAssets?.length ? <>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionHeaderText}>{'ASSETS'}</Text>
            </View>
            {[...searchedAssets, ...filteredAssets]
              ?.sort((prev: AssetInstance, next: AssetInstance) => prev.name
                .localeCompare(next.name))
              .map((asset: AssetInstance, index: number) => {
                const currentlySelected: boolean = selectedAsset?.id === asset.id;
                return <TouchableOpacity
                  key={`filtered_asset${asset.id}`}
                  activeOpacity={currentlySelected ? styles.currentSelection.opacity : undefined}
                  style={[styles.assetResult, currentlySelected && styles.currentSelection]}
                  ref={(ref) => refs.push(ref)}
                  onFocus={() => setFocusIndex(index)}
                  onPress={() => {
                    if (currentlySelected) return;
                    selectTemplate(null);
                    selectAsset(asset);
                    setSearchValue('');
                    showAssets(true);
                  }}
                  testID={`searched-asset#${index + 1}`}
                >
                  <Text style={styles.searchResultTitle}>
                    {asset.name}
                    {currentlySelected && <Text style={styles.currentlySelectedText}>
                      {' (currently selected)'}
                    </Text>}
                  </Text>
                  <Text style={styles.searchResultDescription}>{asset.templateName}</Text>
                </TouchableOpacity>;
              })}
          </> : null}
          {filteredTemplates?.length ? <>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionHeaderText}>{'TEMPLATES'}</Text>
            </View>
            {filteredTemplates.map((template: Template, index: number) => {
              const currentlySelected: boolean = !selectedAsset
                && template.id === selectedTemplate?.id;
              return <TouchableOpacity
                key={`filtered_template${template.id}`}
                activeOpacity={currentlySelected ? styles.currentSelection.opacity : undefined}
                style={[styles.templateResult, currentlySelected && styles.currentSelection]}
                ref={(ref) => refs.push(ref)}
                onFocus={() => setFocusIndex(filteredAssets?.length ?? 0 + index)}
                onPress={() => {
                  if (currentlySelected) return;
                  if (assetsShown) selectTemplate(template);
                  else {
                    const timeout = setTimeout(() => {
                      selectTemplate(template);
                      clearTimeout(timeout);
                    }, 200);
                  }
                  showAssets(true);
                  selectAsset(null);
                  selectTemplateGroup(null);
                  setSearchValue('');
                }}
                testID={`searched-template#${index + 1}`}
              >
                <GridIconSmall style={styles.templateIcon} />
                <Text style={styles.searchResultTitle}>
                  {template.name}
                  {currentlySelected && <Text style={styles.currentlySelectedText}>
                    {' (currently selected)'}
                  </Text>}
                </Text>
              </TouchableOpacity>;
            })}
          </> : null}
          {filteredTemplateGroups?.length ? <>
            <View style={styles.sectionHeader}>
              <Text style={styles.sectionHeaderText}>{'TEMPLATE GROUPS'}</Text>
            </View>
            {filteredTemplateGroups.map((group: TemplateGroup, index: number) => {
              const currentlySelected: boolean = !selectedAsset
                && selectedTemplateGroup === group.ButtonPrototypeGroupId;
              return <TouchableOpacity
                key={`filtered_template_group${group.ButtonPrototypeGroupId}`}
                activeOpacity={currentlySelected ? styles.currentSelection.opacity : undefined}
                style={[styles.templateResult, currentlySelected && styles.currentSelection]}
                ref={(ref) => refs.push(ref)}
                onFocus={() => setFocusIndex((filteredAssets?.length ?? 0)
                  + (filteredTemplates?.length ?? 0) + index)}
                onPress={() => {
                  if (currentlySelected) return;
                  if (!assetsShown) selectTemplateGroup(group.ButtonPrototypeGroupId);
                  else {
                    const timeout = setTimeout(() => {
                      selectTemplateGroup(group.ButtonPrototypeGroupId);
                      clearTimeout(timeout);
                    }, 200);
                  }
                  showAssets(false);
                  selectAsset(null);
                  selectTemplate(null);
                  setSearchValue('');
                }}
                testID={`searched-template-group#${index + 1}`}
              >
                <Text style={styles.searchResultTitle}>
                  {group.Name}
                  {currentlySelected && <Text style={styles.currentlySelectedText}>
                    {' (currently selected)'}
                  </Text>}
                </Text>
              </TouchableOpacity>;
            })}
          </> : null}
          {!filteredAssets?.length && !filteredTemplates?.length && !filteredTemplateGroups?.length
            && <View style={styles.assetResult}>
              <Text style={styles.searchResultTitle}>{'No results found'}</Text>
            </View>}
        </ScrollView>
      </View>
    </View> : null;
};

const styles = StyleSheet.create({
  wrapper: {
    height: 0,
    zIndex: 2,
  },
  container: {
    position: 'absolute',
    top: 8,
    width: '100%',
    paddingHorizontal: 24,
  },
  content: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: Colors.light.hereworksBlue100,
    backgroundColor: '#FFF',
    maxHeight: 317,
  },
  sectionHeader: {
    backgroundColor: Colors.light.hereworksWhite400,
    width: '100%',
    borderTopStartRadius: 10,
    borderTopEndRadius: 10,
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  sectionHeaderText: {
    color: Colors.light.hereworksBlack500,
    fontSize: 12,
    lineHeight: 20,
    fontFamily: 'Roboto_500Medium',
    marginHorizontal: 12,
    marginVertical: 6,
  },
  assetResult: {
    paddingHorizontal: 12,
    paddingVertical: 9,
  },
  templateResult: {
    paddingVertical: 18,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
  },
  searchResultTitle: {
    fontSize: 14,
    lineHeight: 21,
    color: Colors.light.hereworksBlack600,
    fontFamily: 'Poppins_400Regular',
  },
  searchResultDescription: {
    fontSize: 12,
    lineHeight: 14.06,
    color: Colors.light.hereworksBlack400,
    fontFamily: 'Roboto_400Regular',
    marginTop: 4,
  },
  templateIcon: {
    marginEnd: 10,
  },
  currentlySelectedText: {
    fontSize: 12,
    lineHeight: 14.06,
  },
  currentSelection: {
    opacity: 0.5,
  },
});

export default SearchResultsPane;
